import React, { Component } from 'react'
import { withApollo } from 'react-apollo'
import gql from 'graphql-tag'
import { store } from '../../stores/reduxStore'
import { Card, CardHeader, CardBody, Row, Col, Table, Badge, Input, FormGroup, Label } from 'reactstrap'
import { Line } from 'react-chartjs-2'
import { RiseLoader } from 'react-spinners'

const PROJECTS_QUERY = gql`
  query DefectivenessTrendProjects {
    projects {
      list { id name project_companies }
    }
  }
`

const PERSON_QUERY = gql`
  query DefectivenessTrendPerson($person_id: ID!) {
    person(id: $person_id) { company_ids }
  }
`

const DATA_QUERY = gql`
  query StatsDefectivenessTrend($project_id: ID!) {
    stats_defectiveness_trend(project_id: $project_id) {
      project_id
      project_name
      monthly_data { month total_complete total_failed defect_rate }
      welder_trends {
        person_id
        welder_name
        trend
        monthly { month defect_rate total_complete }
      }
    }
  }
`

const COLORS = [
  'rgba(255,99,132,1)', 'rgba(54,162,235,1)', 'rgba(255,206,86,1)',
  'rgba(75,192,192,1)', 'rgba(153,102,255,1)', 'rgba(255,159,64,1)',
  'rgba(199,199,199,1)', 'rgba(83,102,255,1)', 'rgba(40,159,64,1)'
]

const trendBadge = (t) => {
  const map = { increasing: ['danger', 'fa-arrow-up'], decreasing: ['success', 'fa-arrow-down'], stable: ['secondary', 'fa-minus'] }
  const [color, icon] = map[t] || ['secondary', 'fa-minus']
  return <Badge color={color}><i className={`fa ${icon}`} /> {t}</Badge>
}

class DefectivenessTrend extends Component {
  constructor(props) {
    super(props)
    this.state = {
      projects: [], userCompanyIds: [], selectedProjectId: null,
      data: null, loading: false, projectsLoading: true, error: null,
      sortCol: 'welder_name', sortDir: 'asc', filterTrend: null
    }
  }

  componentDidMount() {
    const personId = store.getState().user.data.person_id
    const params = new URLSearchParams(this.props.location && this.props.location.search)
    const urlProjectId = params.get('project_id')
    Promise.all([
      this.props.client.query({ query: PROJECTS_QUERY, fetchPolicy: 'network-only' }),
      this.props.client.query({ query: PERSON_QUERY, variables: { person_id: personId }, fetchPolicy: 'network-only' })
    ]).then(([projectsRes, personRes]) => {
      this.setState({
        projects: projectsRes.data.projects.list,
        userCompanyIds: (personRes.data.person.company_ids || []).map(String),
        projectsLoading: false
      }, () => { if (urlProjectId) this.handleProjectChange(urlProjectId) })
    }).catch(e => this.setState({ error: e.message, projectsLoading: false }))
  }

  handleProjectChange(projectId) {
    if (!projectId) {
      this.setState({ selectedProjectId: null, data: null })
      this.props.history && this.props.history.replace('/reports/defectiveness-trend')
      return
    }
    this.setState({ selectedProjectId: projectId, data: null, loading: true, error: null })
    this.props.history && this.props.history.replace(`/reports/defectiveness-trend?project_id=${projectId}`)
    this.props.client.query({ query: DATA_QUERY, fetchPolicy: 'network-only', variables: { project_id: projectId } })
      .then(({ data }) => this.setState({ data: data.stats_defectiveness_trend[0] || null, loading: false }))
      .catch(e => this.setState({ error: e.message, loading: false }))
  }

  sortWelders(welders) {
    const { sortCol, sortDir } = this.state
    return [...welders].sort((a, b) => {
      let av, bv
      if (sortCol === 'welder_name') { av = a.welder_name; bv = b.welder_name }
      else if (sortCol === 'trend') { av = a.trend; bv = b.trend }
      else {
        const am = a.monthly.find(m => m.month === sortCol); av = am ? am.defect_rate : -1
        const bm = b.monthly.find(m => m.month === sortCol); bv = bm ? bm.defect_rate : -1
      }
      if (av < bv) return sortDir === 'asc' ? -1 : 1
      if (av > bv) return sortDir === 'asc' ? 1 : -1
      return 0
    })
  }

  handleSort(col) {
    this.setState(s => ({ sortCol: col, sortDir: s.sortCol === col && s.sortDir === 'asc' ? 'desc' : 'asc' }))
  }

  render() {
    const { projects, userCompanyIds, selectedProjectId, data, loading, projectsLoading, error } = this.state

    const filteredProjects = projects.filter(p => {
      if (!userCompanyIds.length) return true
      try {
        const pc = typeof p.project_companies === 'string' ? JSON.parse(p.project_companies) : (p.project_companies || [])
        return pc.some(c => userCompanyIds.includes(String(c.id)))
      } catch (e) { return false }
    })

    return (
      <div className="animated fadeIn">
        <Card>
          <CardHeader><strong><i className="fa fa-line-chart mr-2" />Defectiveness Trend</strong></CardHeader>
          <CardBody style={{ position: 'relative', minHeight: '200px' }}>
            {(loading || projectsLoading) && <div className="spinner-item" id="loader-bg"><RiseLoader color="#20a8d8" loading={true} /></div>}
            {error && <div className="text-danger">Error: {error}</div>}

            <FormGroup row className="mb-3">
              <Label sm={2}>Project</Label>
              <Col sm={4}>
                <Input type="select" value={selectedProjectId || ''}
                  onChange={e => this.handleProjectChange(e.target.value || null)}>
                  <option value="">— select project —</option>
                  {filteredProjects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                </Input>
              </Col>
            </FormGroup>

            {!selectedProjectId && !loading && (
              <div className="text-center text-muted py-5">Select a project to view defectiveness trend</div>
            )}

            {data && !loading && (() => {
              const selected = data

              const projectChartData = {
                labels: selected.monthly_data.map(d => d.month),
                datasets: [{
                  label: 'Defect rate %',
                  data: selected.monthly_data.map(d => d.defect_rate),
                  borderColor: 'rgba(255,99,132,1)',
                  backgroundColor: 'rgba(255,99,132,0.1)',
                  fill: true,
                  tension: 0.3
                }]
              }

              const welderChartData = {
                labels: [...new Set(selected.welder_trends.flatMap(w => w.monthly.map(m => m.month)))].sort(),
                datasets: selected.welder_trends.slice(0, 8).map((w, i) => ({
                  label: w.welder_name,
                  data: w.monthly.map(m => m.defect_rate),
                  borderColor: COLORS[i % COLORS.length],
                  fill: false,
                  tension: 0.3,
                  pointRadius: 3
                }))
              }

              const allMonths = [...new Set(selected.welder_trends.flatMap(w => w.monthly.map(m => m.month)))].sort()
              const { sortCol, sortDir } = this.state
              const icon = (col) => sortCol === col ? (sortDir === 'asc' ? ' ▲' : ' ▼') : ' ⇅'
              const th = (col, label) => (
                <th key={col} style={{ cursor: 'pointer', whiteSpace: 'nowrap' }} onClick={() => this.handleSort(col)}>
                  {label}{icon(col)}
                </th>
              )

              return (
                <div>
                  <Row>
                    <Col md="6">
                      <h6>Project defect rate by month</h6>
                      <Line data={projectChartData} options={{
                        maintainAspectRatio: true,
                        scales: { yAxes: [{ ticks: { beginAtZero: true }, scaleLabel: { display: true, labelString: 'Defect rate %' } }] }
                      }} />
                    </Col>
                    <Col md="6">
                      <h6>Welder defect rate by month (top 8)</h6>
                      <Line data={welderChartData} options={{
                        maintainAspectRatio: true,
                        scales: { yAxes: [{ ticks: { beginAtZero: true }, scaleLabel: { display: true, labelString: 'Defect rate %' } }] }
                      }} />
                    </Col>
                  </Row>

                  <h6 className="mt-4">Welder trends</h6>
                  <FormGroup row className="mb-2">
                    <Label sm={2}>Filter by trend</Label>
                    <Col sm={3}>
                      <Input type="select" value={this.state.filterTrend || ''}
                        onChange={e => this.setState({ filterTrend: e.target.value || null })}>
                        <option value="">— all —</option>
                        <option value="increasing">Increasing</option>
                        <option value="decreasing">Decreasing</option>
                        <option value="stable">Stable</option>
                      </Input>
                    </Col>
                  </FormGroup>

                  <Table bordered striped responsive size="sm">
                    <thead>
                      <tr>{th('welder_name', 'Welder')}{th('trend', 'Trend')}{allMonths.map(m => th(m, m))}</tr>
                    </thead>
                    <tbody>
                      {this.sortWelders(selected.welder_trends.filter(w => !this.state.filterTrend || w.trend === this.state.filterTrend)).map(w => {
                        const byMonth = w.monthly.reduce((acc, m) => { acc[m.month] = m; return acc }, {})
                        return (
                          <tr key={w.person_id}>
                            <td>{w.welder_name}</td>
                            <td>{trendBadge(w.trend)}</td>
                            {allMonths.map(m => {
                              const d = byMonth[m]
                              return (
                                <td key={m} style={{ color: d && d.defect_rate > 2 ? 'red' : d && d.defect_rate > 0 ? 'orange' : 'inherit' }}>
                                  {d && d.defect_rate > 0 ? `${d.defect_rate}%` : '—'}
                                </td>
                              )
                            })}
                          </tr>
                        )
                      })}
                    </tbody>
                  </Table>

                  <div className="mt-3 p-3 border rounded bg-light" style={{ fontSize: '12px' }}>
                    <strong>How it's calculated:</strong>
                    <ul className="mb-0 mt-1 pl-3">
                      <li><strong>Defect rate %</strong> — <em>failed mm / completed mm × 100</em>, based on inspection logs (last 12 months for project chart, last 6 months for welders)</li>
                      <li><strong>Welder assignment</strong> — welder is the person from the most recent welding/grinding log on the weld, with generation ≤ inspection generation</li>
                      <li><strong>Trend (welder)</strong> — compares first vs last month defect rate: last &gt; first = increasing, last &lt; first = decreasing, equal = stable</li>
                    </ul>
                  </div>
                </div>
              )
            })()}
          </CardBody>
        </Card>
      </div>
    )
  }
}

export default withApollo(DefectivenessTrend)



// WEBPACK FOOTER //
// ./src/components/Report/DefectivenessTrend.js